import { AxiosInstance } from "axios";
import SurveyDispatch from "../models/surveyDispatch";
import {
  CreateSurveyDispatchResponse,
  CustomQuestionAddBody,
  CustomQuestionUpdateBody,
  GetJobRolesResponse,
  GetSurveyDispatchResponse,
  GetSurveyDispatchesResponse,
  GetSurveyJobLevels,
  GetSurveyResponse,
  InvalidRespondentsResponse,
  PreviewSurveyResponse,
  SubmitSurveyBody,
  Survey,
  SurveyEmailBody,
  SurveyEmailLanguage,
  SurveyEmailResponse,
  UpdateJobRoleBody,
  UpdateSurveyDispatchBody,
} from "../types/survey";
import { getApiClient } from "../utils/api.utils";

export class SurveyService {
  client: AxiosInstance;

  constructor(useAuth = true) {
    this.client = getApiClient(useAuth);
  }

  /** SURVEY API CALLS */
  getSurveys = async () => {
    const response = await this.client.get<Survey[]>("/surveys");
    return response.data;
  };

  getSurvey = async (dispatchToken: string) => {
    const response = await this.client.get<GetSurveyResponse>(
      `/surveys/${dispatchToken}/start/`
    );

    return response.data;
  };

  previewSurvey = async (id: string) => {
    if (id.startsWith("SUR.")) {
      const response = await this.client.get<PreviewSurveyResponse>(
        `/surveys/${id}/preview/`
      );

      return response.data;
    }

    const response = await this.client.get<PreviewSurveyResponse>(
      `/surveys/dispatches/${id}/preview/`
    );

    return response.data;
  };

  submitSurvey = async (dispatchToken: string, body: SubmitSurveyBody) => {
    if (!body.answers) {
      throw Error("No answers in submitSurvey body");
    }

    const response = await this.client.post(
      `/surveys/${dispatchToken}/submit/`,
      body
    );

    return response.data;
  };

  /** DISPATCH API CALLS */

  getDispatches = async () => {
    const response = await this.client.get<GetSurveyDispatchesResponse[]>(
      `/surveys/dispatches/`
    );

    return response.data;
  };

  getSurveyDispatches = async (surveyId: string) => {
    const response = await this.client.get<GetSurveyDispatchesResponse[]>(
      `/surveys/${surveyId}/dispatches/`
    );

    return response.data;
  };

  getDispatch = async (dispatchId: string) => {
    const response = await this.client.get<GetSurveyDispatchResponse>(
      `/surveys/dispatches/${dispatchId}`
    );

    return response.data;
  };

  getSurveyDispatch = async (surveyId: string, dispatchId: string) => {
    const response = await this.client.get<GetSurveyDispatchResponse>(
      `/surveys/${surveyId}/dispatches/${dispatchId}`
    );

    return response.data;
  };

  createDispatch = async (surveyId: string) => {
    const response = await this.client.post<CreateSurveyDispatchResponse>(
      `/surveys/${surveyId}/dispatches/`
    );

    return response.data;
  };

  editDispatch = async (
    dispatch: SurveyDispatch,
    body: UpdateSurveyDispatchBody
  ) => {
    const response = await this.client.put<CreateSurveyDispatchResponse>(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}`,
      body
    );

    return response.data;
  };

  deleteDispatch = async (dispatch: SurveyDispatch) => {
    const response = await this.client.delete(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}`
    );

    return response.data;
  };

  /** CUSTOM QUESTIONS API CALLS */

  getAllCustomQuestions = async (surveyId: string) => {
    const response = await this.client.get(
      `/surveys/${surveyId}/custom-questions/`
    );

    return response.data;
  };

  getDispatchCustomQuestions = async (dispatch: SurveyDispatch) => {
    const response = await this.client.get(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/custom-questions/`
    );

    return response.data;
  };

  addCustomQuestion = async (
    dispatch: SurveyDispatch,
    body: CustomQuestionAddBody
  ) => {
    const response = await this.client.post(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/custom-questions/`,
      body
    );

    return response.data;
  };

  updateCustomQuestion = async (
    dispatch: SurveyDispatch,
    questionId: string,
    body: CustomQuestionUpdateBody
  ) => {
    const response = await this.client.put(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/custom-questions/${questionId}`,
      body
    );

    return response.data;
  };

  deleteCustomQuestion = async (
    questionId: string,
    dispatch: SurveyDispatch
  ) => {
    const response = await this.client.delete(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/custom-questions/${questionId}`
    );

    return response.data;
  };

  /** JOB ROLES API CALLS */

  getSurveyJobLevels = async (surveyId: string) => {
    const response = await this.client.get<GetSurveyJobLevels>(
      `/surveys/${surveyId}/job-levels/`
    );

    return response.data;
  };

  getTenantJobRoles = async (surveyId: string) => {
    const response = await this.client.get<GetJobRolesResponse>(
      `/surveys/${surveyId}/job-roles/`
    );

    return response.data;
  };

  getDispatchJobRoles = async (dispatch: SurveyDispatch) => {
    const response = await this.client.get<GetJobRolesResponse>(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/job-roles/`
    );

    return response.data;
  };

  addDispatchJobRole = async (
    dispatch: SurveyDispatch,
    jobRole: UpdateJobRoleBody
  ) => {
    const response = await this.client.post(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/job-roles/`,
      jobRole
    );

    return response.data;
  };

  updateDispatchJobRole = async (
    dispatch: SurveyDispatch,
    jobRole: UpdateJobRoleBody
  ) => {
    const response = await this.client.put<UpdateJobRoleBody>(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/job-roles/${jobRole.id}`,
      jobRole
    );
    return response.data;
  };

  deleteJobRole = async (dispatch: SurveyDispatch, jobRoleId: string) => {
    const response = await this.client.delete(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/job-roles/${jobRoleId}`
    );

    return response.data;
  };

  /** OTHER API CALLS */

  getSupportedSurveyEmailLanguages = async (surveyId: string) => {
    const response = await this.client.get<SurveyEmailLanguage[]>(
      `/surveys/${surveyId}/survey-emails/languages/`
    );

    return response.data;
  };

  getSurveyEmail = async (dispatch: SurveyDispatch) => {
    const response = await this.client.get<SurveyEmailResponse>(
      `surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/survey-emails/`
    );

    return response.data;
  };

  updateSurveyEmail = async (
    dispatch: SurveyDispatch,
    surveyEmailBody: SurveyEmailBody
  ) => {
    const response = await this.client.post(
      `surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/survey-emails/`,
      surveyEmailBody
    );

    return response.data;
  };

  getInvalidRespondents = async (dispatch: SurveyDispatch) => {
    const response = await this.client.get<InvalidRespondentsResponse>(
      `/surveys/${dispatch.surveyId}/dispatches/${dispatch.id}/invalid-respondents/`
    );

    return response.data;
  };
}
